import { useState } from 'react';
import { MESES_COBRADOS_EN_PLAN_ANUAL, TURNOS_POR_MES_PLAN_BASICO, type CuentaProfesional, type PlanPublico } from '@medchoice/shared';
import { ApiError } from '../../api/client';
import { consultorio } from '../../api/consultorio';
import { publico } from '../../api/publico';
import { usePedido } from '../../api/usePedido';
import { useSesion } from '../../session/Sesion';
import { AvisoError, Boton, Cargando, FalloDeCarga, Insignia, Kpi, Modal, Tarjeta, useAvisos } from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { fechaCorta, pesos } from '../../ui/format';
import { Seccion } from './piezas';

export function Cuenta() {
  const { salir } = useSesion();
  const cuenta = usePedido(() => consultorio.cuenta(), []);
  const catalogos = usePedido(() => publico.catalogos(), []);
  return (
    <SeccionPanel titulo="Cuenta y plan" descripcion="Tu plan y el resumen de facturación del mes"
      acciones={<Boton tamano="chico" variante="secundario" icono="log-out" onClick={() => void salir()}>Cerrar sesión</Boton>}>
      {cuenta.estado === 'cargando' && !cuenta.datos && <Cargando />}
      {cuenta.estado === 'error' && <FalloDeCarga mensaje={cuenta.error ?? ''} alReintentar={cuenta.recargar} />}
      {cuenta.datos && catalogos.datos && (
        <Resumen cuenta={cuenta.datos} planes={catalogos.datos.planes} alCambiar={cuenta.recargar} />
      )}
    </SeccionPanel>
  );
}

function Resumen({ cuenta, planes, alCambiar }: { cuenta: CuentaProfesional; planes: PlanPublico[]; alCambiar: () => void }) {
  const [anual, setAnual] = useState(cuenta.suscripcion.anual);
  const [elegido, setElegido] = useState<PlanPublico | null>(null);
  const { plan, mes } = cuenta;

  return (
    <div className="pila config">
      <div className="kpis">
        <Kpi etiqueta="Turnos del mes" valor={mes.limite ? `${mes.turnos} de ${mes.limite}` : mes.turnos} icono="calendar"
          pista={mes.limite ? 'El plan Básico tiene un tope mensual' : undefined} />
        <Kpi etiqueta="Facturado este mes" valor={pesos(mes.facturado)} icono="wallet" pista="Turnos atendidos" />
        <Kpi etiqueta="Costo del plan" valor={plan.precio_mensual === 0 ? 'Gratis' : pesos(plan.precio_mensual)} icono="credit-card"
          pista={cuenta.suscripcion.anual ? `Anual, vence el ${fechaCorta(cuenta.suscripcion.fecha_fin ?? '')}` : 'Por mes'} />
        <Kpi etiqueta="Comisión por señas" valor={pesos(mes.comisiones)} icono="shield-check"
          pista={`${Math.round(plan.comision_sena * 100)}% de cada seña acreditada`} />
      </div>

      <Tarjeta>
        <Seccion titulo="Tu plan" descripcion={`Estás en el plan ${plan.nombre} desde el ${fechaCorta(cuenta.suscripcion.fecha_inicio)}.`}>
          <div className="planes__periodo" role="group" aria-label="Período de facturación">
            <Boton variante={anual ? 'secundario' : 'primario'} tamano="chico" onClick={() => setAnual(false)}>Mensual</Boton>
            <Boton variante={anual ? 'primario' : 'secundario'} tamano="chico" onClick={() => setAnual(true)}>
              Anual <Insignia tono="coral" tamano="chico">{12 - MESES_COBRADOS_EN_PLAN_ANUAL} meses gratis</Insignia>
            </Boton>
          </div>
          <div className="planes-cuenta">
            {planes.map((p) => {
              const esActual = p.id_plan === plan.id_plan && (p.precio_mensual === 0 || anual === cuenta.suscripcion.anual);
              const precio = anual ? p.precio_anual : p.precio_mensual;
              return (
                <div key={p.id_plan} className={`plan-cuenta${esActual ? ' plan-cuenta--actual' : ''}`}>
                  <strong>{p.nombre}</strong>
                  <span className="plan-cuenta__precio">
                    {p.precio_mensual === 0 ? 'Gratis' : `${pesos(precio)} ${anual ? 'por año' : 'por mes'}`}
                  </span>
                  <span className="nota">{p.descripcion}</span>
                  <span className="nota">Comisión del {Math.round(p.comision_sena * 100)}% por seña</span>
                  <Boton anchoCompleto variante={esActual ? 'secundario' : 'primario'} disabled={esActual} onClick={() => setElegido(p)}>
                    {esActual ? 'Tu plan' : p.precio_mensual === 0 ? 'Pasar al Básico' : `Pasar a ${p.nombre}`}
                  </Boton>
                </div>
              );
            })}
          </div>
        </Seccion>
      </Tarjeta>

      <Tarjeta>
        <Seccion titulo="Comisión sobre las señas"
          descripcion="Es un recupero de costos, no un margen: cubre la pasarela de pago y la mensajería.">
          <p className="nota">
            Este mes MedChoice retuvo {pesos(mes.comisiones)} de tus señas. El porcentaje depende del plan: cambiar de plan
            no toca las señas que ya cobraste.
          </p>
        </Seccion>
      </Tarjeta>

      {elegido && (
        <ModalCambioDePlan plan={elegido} anual={anual && elegido.precio_mensual > 0} alCerrar={() => setElegido(null)} alCambiar={alCambiar} />
      )}
    </div>
  );
}

function ModalCambioDePlan({ plan, anual, alCerrar, alCambiar }: {
  plan: PlanPublico; anual: boolean; alCerrar: () => void; alCambiar: () => void;
}) {
  const { mostrarAviso } = useAvisos();
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const monto = anual ? plan.precio_mensual * MESES_COBRADOS_EN_PLAN_ANUAL : plan.precio_mensual;

  const contratar = async () => {
    setEnviando(true);
    try {
      await consultorio.contratar({ plan: plan.id_plan, anual });
      mostrarAviso({ tono: 'exito', titulo: `Ya estás en el plan ${plan.nombre}` });
      alCerrar();
      alCambiar();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'No pudimos cambiar el plan. Probá de nuevo.');
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Modal abierto alCerrar={alCerrar} titulo={`Pasar al plan ${plan.nombre}`}
      pie={<>
        <Boton variante="secundario" onClick={alCerrar}>Cancelar</Boton>
        <Boton cargando={enviando} onClick={() => void contratar()}>Confirmar</Boton>
      </>}>
      <AvisoError>{error}</AvisoError>
      <p className="nota">
        {monto === 0
          ? `Tu plan pago se cancela hoy y seguís con el Básico, que es gratis y admite ${TURNOS_POR_MES_PLAN_BASICO} turnos por mes.`
          : `Se cobran ${pesos(monto)} ${anual ? 'por un año' : 'este mes'} con la tarjeta de tu cuenta. El cobro está simulado.`}
      </p>
    </Modal>
  );
}
